export type Complex = { re: number; im: number };

export type ComplexMatrix2x2 = [[Complex, Complex], [Complex, Complex]];

export type QubitState = {
  alpha: Complex;
  beta: Complex;
};

export type ChangeMeta = {
  source: "gate" | "drag" | "reset" | "external";
  historyIndex?: number;
  viewMode?: "manual" | "ambient-pan";
};

export type ValidationResult = {
  valid: boolean;
  error?: string;
  norm?: number;
};

const EPSILON = 1e-12;

export const C0: Complex = { re: 0, im: 0 };
export const C1: Complex = { re: 1, im: 0 };
export const I: Complex = { re: 0, im: 1 };

export function c(re: number, im = 0): Complex {
  return { re, im };
}

export function add(a: Complex, b: Complex): Complex {
  return { re: a.re + b.re, im: a.im + b.im };
}

export function sub(a: Complex, b: Complex): Complex {
  return { re: a.re - b.re, im: a.im - b.im };
}

export function mul(a: Complex, b: Complex): Complex {
  return {
    re: a.re * b.re - a.im * b.im,
    im: a.re * b.im + a.im * b.re,
  };
}

export function conj(a: Complex): Complex {
  return { re: a.re, im: -a.im };
}

export function abs2(a: Complex): number {
  return a.re * a.re + a.im * a.im;
}

export function scale(a: Complex, s: number): Complex {
  return { re: a.re * s, im: a.im * s };
}

export function approxEq(a: number, b: number, tolerance = 1e-6): boolean {
  return Math.abs(a - b) <= tolerance;
}

function matrixMultiply(a: ComplexMatrix2x2, b: ComplexMatrix2x2): ComplexMatrix2x2 {
  return [
    [add(mul(a[0][0], b[0][0]), mul(a[0][1], b[1][0])), add(mul(a[0][0], b[0][1]), mul(a[0][1], b[1][1]))],
    [add(mul(a[1][0], b[0][0]), mul(a[1][1], b[1][0])), add(mul(a[1][0], b[0][1]), mul(a[1][1], b[1][1]))],
  ];
}

function dagger(m: ComplexMatrix2x2): ComplexMatrix2x2 {
  return [
    [conj(m[0][0]), conj(m[1][0])],
    [conj(m[0][1]), conj(m[1][1])],
  ];
}

export function isUnitary(matrix: ComplexMatrix2x2, tolerance = 1e-6): ValidationResult {
  const product = matrixMultiply(dagger(matrix), matrix);
  const identity: ComplexMatrix2x2 = [[C1, C0], [C0, C1]];

  const delta = [
    [sub(product[0][0], identity[0][0]), sub(product[0][1], identity[0][1])],
    [sub(product[1][0], identity[1][0]), sub(product[1][1], identity[1][1])],
  ] as const;

  const frobeniusNorm = Math.sqrt(
    abs2(delta[0][0]) + abs2(delta[0][1]) + abs2(delta[1][0]) + abs2(delta[1][1]),
  );

  if (!Number.isFinite(frobeniusNorm)) {
    return { valid: false, error: "Matrix contains invalid numbers." };
  }

  if (frobeniusNorm > tolerance) {
    return {
      valid: false,
      error: `Matrix is not unitary (||U†U - I|| = ${frobeniusNorm.toExponential(2)}).`,
      norm: frobeniusNorm,
    };
  }

  return { valid: true, norm: frobeniusNorm };
}

export function normalizeState(state: QubitState): QubitState {
  const norm = Math.sqrt(abs2(state.alpha) + abs2(state.beta));
  if (!Number.isFinite(norm) || norm < EPSILON) {
    return { alpha: C1, beta: C0 };
  }
  return {
    alpha: scale(state.alpha, 1 / norm),
    beta: scale(state.beta, 1 / norm),
  };
}

export function applyGate(state: QubitState, matrix: ComplexMatrix2x2): QubitState {
  const next = {
    alpha: add(mul(matrix[0][0], state.alpha), mul(matrix[0][1], state.beta)),
    beta: add(mul(matrix[1][0], state.alpha), mul(matrix[1][1], state.beta)),
  };
  return normalizeState(next);
}

export function stateToBloch(state: QubitState): { x: number; y: number; z: number } {
  const normalized = normalizeState(state);
  const alphaConjBeta = mul(conj(normalized.alpha), normalized.beta);
  return {
    x: 2 * alphaConjBeta.re,
    y: 2 * alphaConjBeta.im,
    z: abs2(normalized.alpha) - abs2(normalized.beta),
  };
}

export function blochToState(vector: { x: number; y: number; z: number }): QubitState {
  const mag = Math.hypot(vector.x, vector.y, vector.z) || 1;
  const x = vector.x / mag;
  const y = vector.y / mag;
  const z = vector.z / mag;

  const theta = Math.acos(Math.min(1, Math.max(-1, z)));
  const phi = Math.atan2(y, x);
  const alpha = c(Math.cos(theta / 2), 0);
  const sinHalf = Math.sin(theta / 2);
  const beta = c(sinHalf * Math.cos(phi), sinHalf * Math.sin(phi));
  return normalizeState({ alpha, beta });
}

export function complexToString(value: Complex, digits = 3): string {
  const re = Math.abs(value.re) < EPSILON ? 0 : value.re;
  const im = Math.abs(value.im) < EPSILON ? 0 : value.im;

  if (im === 0) return re.toFixed(digits);
  if (re === 0) return `${im.toFixed(digits)}i`;
  const sign = im >= 0 ? "+" : "-";
  return `${re.toFixed(digits)} ${sign} ${Math.abs(im).toFixed(digits)}i`;
}

export function parseComplex(input: string): Complex | null {
  const trimmed = input.trim().toLowerCase().replace(/\s+/g, "");
  if (!trimmed) return null;

  if (trimmed === "i" || trimmed === "+i") return { re: 0, im: 1 };
  if (trimmed === "-i") return { re: 0, im: -1 };

  const pureReal = Number(trimmed);
  if (!Number.isNaN(pureReal) && Number.isFinite(pureReal)) {
    return { re: pureReal, im: 0 };
  }

  const imagMatch = trimmed.match(/^([+-]?\d*\.?\d+)i$/);
  if (imagMatch?.[1]) {
    const imag = Number(imagMatch[1]);
    return Number.isFinite(imag) ? { re: 0, im: imag } : null;
  }

  const splitIndex = (() => {
    for (let i = trimmed.length - 2; i > 0; i--) {
      const char = trimmed[i];
      if ((char === "+" || char === "-") && trimmed[i - 1] !== "e") return i;
    }
    return -1;
  })();

  if (trimmed.endsWith("i") && splitIndex > 0) {
    const left = trimmed.slice(0, splitIndex);
    const right = trimmed.slice(splitIndex, -1);
    const re = Number(left);
    const im = Number(right);
    if (Number.isFinite(re) && Number.isFinite(im)) {
      return { re, im };
    }
  }

  return null;
}

export function parseMatrixText(input: string): ComplexMatrix2x2 | null {
  const cleaned = input.trim().replace(/[\[\]]/g, "");
  const rows = cleaned
    .split(/[;\n]/)
    .map(row => row.trim())
    .filter(Boolean);
  if (rows.length !== 2) return null;

  const parsedRows = rows.map(row =>
    row
      .split(",")
      .map(cell => parseComplex(cell.trim()))
      .filter((cell): cell is Complex => cell !== null),
  );

  const [row0, row1] = parsedRows;
  if (!row0 || !row1 || row0.length !== 2 || row1.length !== 2) {
    return null;
  }

  const [a, b] = row0;
  const [cCell, d] = row1;
  if (!a || !b || !cCell || !d) return null;

  return [
    [a, b],
    [cCell, d],
  ];
}

export type GateDescriptor = {
  id: string;
  label: string;
  matrix: ComplexMatrix2x2;
};

export function gateRx(theta: number): ComplexMatrix2x2 {
  const cHalf = Math.cos(theta / 2);
  const sHalf = Math.sin(theta / 2);
  return [[c(cHalf, 0), c(0, -sHalf)], [c(0, -sHalf), c(cHalf, 0)]];
}

export function gateRy(theta: number): ComplexMatrix2x2 {
  const cHalf = Math.cos(theta / 2);
  const sHalf = Math.sin(theta / 2);
  return [[c(cHalf, 0), c(-sHalf, 0)], [c(sHalf, 0), c(cHalf, 0)]];
}

export function gateRz(theta: number): ComplexMatrix2x2 {
  return [
    [c(Math.cos(-theta / 2), Math.sin(-theta / 2)), C0],
    [C0, c(Math.cos(theta / 2), Math.sin(theta / 2))],
  ];
}

const INV_SQRT2 = 1 / Math.sqrt(2);

export const BUILT_IN_GATES: GateDescriptor[] = [
  { id: "I", label: "I", matrix: [[C1, C0], [C0, C1]] },
  { id: "X", label: "X", matrix: [[C0, C1], [C1, C0]] },
  { id: "Y", label: "Y", matrix: [[C0, c(0, -1)], [c(0, 1), C0]] },
  { id: "Z", label: "Z", matrix: [[C1, C0], [C0, c(-1, 0)]] },
  { id: "H", label: "H", matrix: [[c(INV_SQRT2, 0), c(INV_SQRT2, 0)], [c(INV_SQRT2, 0), c(-INV_SQRT2, 0)]] },
  { id: "S", label: "S", matrix: [[C1, C0], [C0, I]] },
  { id: "Sdg", label: "S†", matrix: [[C1, C0], [C0, c(0, -1)]] },
  { id: "T", label: "T", matrix: [[C1, C0], [C0, c(Math.SQRT1_2, Math.SQRT1_2)]] },
  { id: "Tdg", label: "T†", matrix: [[C1, C0], [C0, c(Math.SQRT1_2, -Math.SQRT1_2)]] },
];
